import React, { useContext, useEffect, useState } from 'react';

import { groupBy } from 'lodash';

import { CategoryDefinitions, GroupType } from 'loot-core/shared/onboarding';
import { type CategoryGroupEntity } from 'loot-core/types/models';

import { OnboardingMintMappingContext } from '../../../../context/onboarding/OnboardingMintMappingContext';
import { SvgArrowThinRight } from '../../../../icons/v1';
import { CategoryAutocomplete } from '../../../autocomplete/CategoryAutocomplete';
import { Button, ButtonWithLoading } from '../../../common/Button';
import { View } from '../../../common/View';

type OnboardingMintCategoriesProps = {
  onBack?: Function;
  onImport?: Function;
};

export function OnboardingMintCategories({
  onBack,
  onImport,
}: OnboardingMintCategoriesProps) {
  const { categorySet, importing } = useContext(OnboardingMintMappingContext);

  const categoryGroupsInitialValue = {};
  const selectedValueInitial = {};

  categorySet.forEach(c => {
    categoryGroupsInitialValue[c] = {
      groupName: 'Uncategorized',
      groupType: null,
      mintCategory: c,
      mappingCategory: 'Uncategorized',
    };
    selectedValueInitial[c] = 'Uncategorized_Uncategorized';
  });

  const [categoryGroups, setCategoryGroups] = useState<{ [key: string]: any }>(
    categoryGroupsInitialValue,
  );
  const [categoryGroupsModified, setCategoryGroupsModified] = useState<
    Array<CategoryGroupEntity & { groupType: GroupType | null }>
  >([]);
  const [selectedValue, setSelectedValue] = useState(selectedValueInitial);

  useEffect(() => {
    const formattedCategoryGroups = CategoryDefinitions.map(d => {
      return {
        ...d,
        id: d.name,
        categories: d.categories.map(c => ({
          ...c,
          cat_group: d.name,
          id: `${d.name}_${c.name}`,
        })),
      };
    });
    setCategoryGroupsModified(formattedCategoryGroups);
  }, []);

  const newCategoryCreateHandler = (
    newCatGroup: CategoryGroupEntity & { groupType: GroupType },
  ) => {
    const _categoryGroupsModified = [...categoryGroupsModified];
    const existingGroupIndex = _categoryGroupsModified.findIndex(
      g => g.name === newCatGroup.name,
    );
    if (existingGroupIndex === -1) {
      setCategoryGroupsModified([..._categoryGroupsModified, newCatGroup]);
    } else {
      const existingGroup = { ..._categoryGroupsModified[existingGroupIndex] };
      existingGroup.categories = [
        ...existingGroup.categories,
        ...newCatGroup.categories,
      ];
      _categoryGroupsModified.splice(existingGroupIndex, 1, existingGroup);
      setCategoryGroupsModified(_categoryGroupsModified);
    }
  };

  const getCategoryGroupsForMintCatHandler = (
    mintCategory: string,
  ): Array<CategoryGroupEntity & { groupType: GroupType | null }> => {
    return [
      {
        id: null,
        name: 'Discard',
        groupType: null,
        categories: [
          { name: 'Discard', cat_group: null, id: 'Discard_Discard' },
        ],
      },
      {
        id: null,
        name: 'Uncategorized',
        groupType: null,
        categories: [
          {
            name: 'Uncategorized',
            cat_group: null,
            id: 'Uncategorized_Uncategorized',
          },
        ],
      },
      ...(selectedValue[mintCategory] &&
      mintCategory !== selectedValue[mintCategory].split('_')[1]
        ? [
            {
              id: null,
              name: 'Use Mint Category Name',
              groupType: null,
              categories: [
                {
                  name: 'Use Mint Category Name',
                  cat_group: null,
                  id: 'useMintCatName',
                },
              ],
            },
          ]
        : []),
      ...categoryGroupsModified,
    ];
  };

  const getPlaceholderValueByMintCategory = (category: string) => {
    const currentValue = selectedValue[category];
    if (currentValue === 'Uncategorized_Uncategorized') {
      return 'Uncategorized';
    } else if (currentValue === 'Discard_Discard') {
      return 'Discard';
    }
    return '';
  };

  return (
    <View style={{ maxHeight: '100vh' }}>
      <View style={{ marginBottom: '24px' }}>
        <View
          style={{
            fontWeight: 'bold',
            alignSelf: 'center',
            fontSize: 18,
            marginBottom: 20,
            flex: 'none',
          }}
        >
          Map Your Mint Categories
        </View>
        <View style={{ maxWidth: '500px', marginBottom: '8px', flex: 'none' }}>
          For each Mint category, please select a Good Steward category so that
          we can match up your Mint transactions into Good Steward.
          <br />
          <br />
          Select “Discard” for transactions that you don’t want to import.
          Selecting “Uncategorized” will import your transactions without a
          category assigned. You can always create your own categories later for
          your uncategorized transactions!
        </View>
        <View>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              padding: '12px',
            }}
          >
            <View
              style={{
                alignItems: 'center',
                flex: 2,
                fontWeight: 500,
              }}
            >
              Mint categories
            </View>
            <View style={{ alignItems: 'center', flex: 1 }} />
            <View style={{ alignItems: 'center', flex: 4, fontWeight: 500 }}>
              Good Steward categories
            </View>
          </View>
          <View style={{ overflowY: 'auto', maxHeight: '300px' }}>
            {categorySet.map((c, i) => (
              <View
                key={c}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  minHeight: 'fit-content',
                  padding: '12px',
                  ...(i !== categorySet.length - 1
                    ? { borderBottom: '1px solid #E5E5E5' }
                    : {}),
                }}
              >
                <View
                  style={{
                    alignItems: 'flex-end',
                    flex: 2,
                    textAlign: 'right',
                  }}
                >
                  {c}
                </View>
                <View style={{ alignItems: 'center', flex: 1 }}>
                  <SvgArrowThinRight width={12} height={12} />
                </View>
                <View style={{ flex: 4 }}>
                  <CategoryAutocomplete
                    closeOnBlur
                    value={
                      [
                        'Uncategorized_Uncategorized',
                        'Discard_Discard',
                      ].includes(selectedValue[c])
                        ? null
                        : selectedValue[c]
                    }
                    creatingCategoryName={c}
                    allowCreateCategory={true}
                    onCreateNewCategory={newCategoryCreateHandler}
                    categoryGroups={getCategoryGroupsForMintCatHandler(c)}
                    onSelect={(groupName, value) => {
                      let mappingCategory = value;
                      if (['Discard', 'Uncategorized'].includes(groupName)) {
                        mappingCategory = groupName;
                      }
                      mappingCategorySelectHandler(
                        groupName,
                        mappingCategory,
                        c,
                      );
                    }}
                    inputProps={{
                      placeholder: getPlaceholderValueByMintCategory(c),
                    }}
                    itemToString={item => {
                      if (['Discard', 'Uncategorized'].includes(item?.name)) {
                        return '';
                      }
                      return item?.name || '';
                    }}
                  />
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>
      <View style={{ gap: 15, minHeight: 'fit-content' }}>
        <View style={{ flexDirection: 'row', marginTop: 10, gap: 5 }}>
          <Button
            type="bare"
            style={{ flex: 1 }}
            disabled={importing}
            onClick={e => onBack?.()}
          >
            Go back
          </Button>

          <ButtonWithLoading
            type="primary"
            style={{ flex: 1 }}
            loading={importing}
            onClick={() => importHandler()}
          >
            Start Planning
          </ButtonWithLoading>
        </View>
      </View>
    </View>
  );

  function mappingCategorySelectHandler(
    catGroupName: string,
    mappingCategory: string,
    mintCategory: string,
  ) {
    if (!mappingCategory || !mintCategory) {
      //For triggering re-render
      setSelectedValue({ ...selectedValue });
    } else {
      setSelectedValue({
        ...selectedValue,
        [mintCategory]: `${catGroupName}_${mappingCategory}`,
      });

      const _categoryGroups = { ...categoryGroups };
      if (mappingCategory !== 'Discard') {
        const catGroup = {
          groupName: catGroupName,
          groupType:
            categoryGroupsModified.find(c => c.name === catGroupName)
              ?.groupType ||
            (catGroupName === 'Uncategorized' ? null : GroupType.Expense),
          mappingCategory,
          mintCategory,
        };
        _categoryGroups[mintCategory] = catGroup;
      } else {
        _categoryGroups[mintCategory] = null;
      }
      setCategoryGroups(_categoryGroups);
    }
  }

  function importHandler() {
    const _categoryGroups = Object.values(categoryGroups).filter(
      cg => cg !== null,
    );
    const groupedCategoryGroups = groupBy(_categoryGroups, 'groupName');
    onImport(groupedCategoryGroups);
  }
}
